import { useCallback } from 'react';

import { DriverSchedulerStoreStorageKeys } from '~constants/dispatch';
import useLiveMapFilters from '~hooks/useLiveMap/useLiveMapFilters';
import { DispatchView } from '~pages/Dispatch/components/constants';
import useOrdersDispatchFilters from '~pages/Dispatch/hooks/useOrdersDispatchFilters';
import { useCompanySelectorVisibility } from '~pages/Settings/useCompanySelectorVisibility';
import { ROUTES as routes } from '~router/routePaths';
import { useStores } from '~store';

const useCompanySelector = () => {
  const { userStore, driverSchedulerStore } = useStores();
  const { handleDispatchOrdersFiltersReset } = useOrdersDispatchFilters();
  const { handleLiveMapFiltersReset } = useLiveMapFilters();

  const routesWithQueryParams = [
    {
      path: routes.dispatch.base,
      queryParams: [
        {
          key: 'view',
          values: [DispatchView.ORDERS, DispatchView.DRIVERS],
        },
      ],
    },
    { path: `${routes.projects}` },
    { path: `${routes.approvals.base}/${routes.approvals.driverDay}` },
    { path: `${routes.approvals.base}/${routes.approvals.approveJobs}` },
    { path: `${routes.approvals.base}/${routes.approvals.approveCharges}` },
    { path: `${routes.approvals.base}/${routes.approvals.driverPay}` },
    { path: `${routes.approvals.base}/${routes.approvals.ticketsReview}` },
    { path: `${routes.live}` },
    { path: `${routes.messages}`, matchNestedPaths: true },
    { path: `${routes.settings.base}/${routes.settings.generalSettings}` },
    { path: `${routes.settings.base}/${routes.settings.userManagement}` },
    { path: `${routes.settings.base}/${routes.settings.drivers}` },
    { path: `${routes.settings.base}/${routes.settings.accounts}` },
    { path: `${routes.settings.base}/${routes.settings.customers}` },
    { path: `${routes.settings.base}/${routes.settings.vendors}` },
    { path: `${routes.settings.base}/${routes.settings.equipmentTypeManagement}` },
    { path: `${routes.settings.base}/${routes.settings.equipmentManagement}` },
    { path: `${routes.settings.base}/${routes.settings.materialsManagement}` },
    { path: `${routes.settings.base}/${routes.settings.ratesManagement}` },
    { path: `${routes.settings.base}/${routes.settings.materialRatesManagement}` },
    { path: `${routes.settings.base}/${routes.settings.addOnCharges}` },
    {
      path: `${routes.settings.base}/${routes.settings.automaticSettlementAddOnManagement}`,
    },
    { path: `${routes.settings.base}/${routes.settings.fuelSurchargeManagement}` },
    { path: `${routes.settings.base}/${routes.settings.sitesManagement}` },
    { path: `${routes.settings.base}/${routes.settings.departmentsManagement}` },
    { path: `${routes.settings.base}/${routes.settings.apiClients}` },
    { path: `${routes.settings.base}/${routes.settings.serviceClassesManagement}` },
    { path: `${routes.settings.base}/${routes.settings.labelsManagement}` },
    { path: `${routes.settings.base}/${routes.settings.complianceDocumentTypes}` },
    { path: `${routes.settings.base}/${routes.settings.overtimeRulesManagement}` },
    {
      path: `${routes.settings.base}/${routes.settings.prevailingWageBenefitsManagement}`,
    },
    { path: `${routes.reports}` },
  ];

  const hasChildrenCompanies = userStore.userCompanies.length > 1;

  const showCompanySelector =
    useCompanySelectorVisibility(routesWithQueryParams) && hasChildrenCompanies;

  const handleDriverSchedulerFiltersReset = useCallback(() => {
    localStorage.removeItem(
      DriverSchedulerStoreStorageKeys.APPLIED_ASSIGNED_JOBS_FILTERS,
    );
    localStorage.removeItem(DriverSchedulerStoreStorageKeys.JOBS_AVAILABLE_OPTIONS);
    driverSchedulerStore.resetAssignedJobs();
    driverSchedulerStore.setUnassignedJobsFilters({});
    driverSchedulerStore.clearUnassignedJobs();
    driverSchedulerStore.clearAssignees();
  }, [driverSchedulerStore]);

  const handleCompanySelectorChange = useCallback(() => {
    handleDispatchOrdersFiltersReset();
    handleLiveMapFiltersReset();
    handleDriverSchedulerFiltersReset();
  }, [
    handleDispatchOrdersFiltersReset,
    handleLiveMapFiltersReset,
    handleDriverSchedulerFiltersReset,
  ]);

  return {
    showCompanySelector,
    handleCompanySelectorChange: showCompanySelector
      ? handleCompanySelectorChange
      : undefined,
  };
};

export default useCompanySelector;
